import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Wand2, Loader2, CheckCircle2 } from 'lucide-react';

const MONTHS = [1, 2, 3, 6, 12];

export default function CompleteDataCard({ api }) {
  const [sources, setSources] = useState([]);
  const [sourceKey, setSourceKey] = useState('');
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null); // { total, matched, unmatched, groups }
  const [groups, setGroups] = useState([]);
  const [fixScan, setFixScan] = useState(null); // { total, fixes }
  const [fixing, setFixing] = useState(false);

  useEffect(() => {
    api.get('/imports/sources').then((r) => {
      const list = r.data.sources || [];
      setSources(list);
      if (list.length) setSourceKey((prev) => (prev && list.some((s) => `${s.type}:${s.id}` === prev) ? prev : `${list[0].type}:${list[0].id}`));
    }).catch(() => {});
  }, [api]);

  const source = sources.find((s) => `${s.type}:${s.id}` === sourceKey);

  const scan = async () => {
    if (!source) { toast.error('Escolha a integração'); return; }
    setScanning(true); setResult(null); setGroups([]);
    try {
      const r = await api.get(`/sigma/login/${source.id}/complete-scan`);
      setResult(r.data);
      // prepara os grupos editáveis (nome + preço + meses)
      const gs = (r.data.groups || []).map((g) => ({
        ...g,
        plan_name: g.suggested_name || g.package_name || 'Plano',
        price: '',
        months: guessMonths(g.suggested_name || g.package_name),
      }));
      setGroups(gs);
      if (r.data.total === 0) toast.success('Todos os clientes já têm plano!');
      else if (gs.length === 0) toast.warning('Não consegui identificar os pacotes no painel.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao analisar');
    } finally { setScanning(false); }
  };

  const apply = async () => {
    if (!groups.length) return;
    // valida preços
    for (const g of groups) {
      if (g.price === '' || isNaN(parseFloat(g.price))) { toast.error(`Informe o preço de "${g.plan_name}"`); return; }
    }
    setApplying(true);
    try {
      const payload = {
        groups: groups.map((g) => ({
          package_id: g.package_id, package_name: g.package_name,
          plan_name: g.plan_name, price: parseFloat(g.price),
          months: parseInt(g.months) || 1, connections: g.connections || 1,
          customer_ids: g.customer_ids, phone_updates: g.phone_updates || [],
        })),
      };
      const r = await api.post(`/sigma/login/${source.id}/complete-apply`, payload);
      toast.success(r.data.message || 'Concluído!');
      setResult(null); setGroups([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao aplicar');
    } finally { setApplying(false); }
  };

  const scanFix = async () => {
    if (!source) { toast.error('Escolha a integração'); return; }
    setFixScan(null);
    try {
      const r = await api.get(`/sigma/login/${source.id}/fix-usernames-scan`);
      setFixScan(r.data);
      if (r.data.total === 0) toast.success('Nenhum usuário para corrigir — está tudo certo!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao analisar'); }
  };
  const applyFix = async () => {
    setFixing(true);
    try {
      const r = await api.post(`/sigma/login/${source.id}/fix-usernames-apply`);
      toast.success(r.data.message || 'Corrigido!');
      setFixScan(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao corrigir'); }
    finally { setFixing(false); }
  };

  const upd = (i, patch) => setGroups((prev) => prev.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Wand2 className="w-4 h-4 text-primary" /> Completar dados dos importados
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Cria e vincula o plano de cada cliente importado que está sem plano, puxando o pacote de cada um do painel. Também atualiza o telefone quando o painel tiver e o cliente não.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs">Integração</Label>
            <Select value={sourceKey} onValueChange={setSourceKey}>
              <SelectTrigger><SelectValue placeholder="Escolha a integração" /></SelectTrigger>
              <SelectContent>
                {sources.map((s) => <SelectItem key={`${s.type}:${s.id}`} value={`${s.type}:${s.id}`}>{s.name} — {s.provider_label || s.type}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={scan} disabled={scanning || !source}>
            {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />} Analisar
          </Button>
        </div>

        {result && result.total > 0 && (
          <div className="text-xs text-muted-foreground">
            {result.total} sem plano · {result.matched} identificado(s) por pacote
            {result.unmatched > 0 && ` · ${result.unmatched} não encontrado(s) no painel`}
          </div>
        )}

        {/* Corrigir usuário trocado pelo apelido */}
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/5 p-3">
          <p className="text-xs font-medium mb-1">🔧 Corrigir usuário (login) trocado pelo apelido</p>
          <p className="text-[11px] text-muted-foreground mb-2">
            Se na importação o login virou o apelido do painel (ex.: "teste") em vez do usuário real, isso corrige em massa: coloca o usuário certo como login e move o apelido para "Nome do cliente".
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={scanFix} disabled={!source}>Analisar</Button>
            {fixScan && fixScan.total > 0 && (
              <Button size="sm" onClick={applyFix} disabled={fixing}>
                {fixing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Corrigir {fixScan.total} cliente(s)
              </Button>
            )}
          </div>
          {fixScan && fixScan.total > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-border/50 divide-y divide-border/40 text-xs">
              {fixScan.fixes.map((f) => (
                <div key={f.id} className="px-2 py-1.5 flex items-center gap-2">
                  <span className="text-red-400 line-through truncate">{f.atual_login}</span>
                  <span className="text-muted-foreground">→</span>
                  <span className="text-emerald-400 font-medium truncate">{f.correto_login}</span>
                </div>
              ))}
              {fixScan.more > 0 && <div className="px-2 py-1 text-muted-foreground">+{fixScan.more} outros…</div>}
            </div>
          )}
        </div>

        {groups.length > 0 && (
          <>
            <div className="space-y-3">
              {groups.map((g, i) => (
                <div key={i} className="rounded-xl border border-border/60 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Pacote no painel: <b className="text-foreground">{g.package_name}</b></span>
                    <span className="text-[11px] bg-primary/10 text-primary rounded-full px-2 py-0.5">{g.count} cliente(s)</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <Label className="text-[11px]">Nome do plano</Label>
                      <Input value={g.plan_name} onChange={(e) => upd(i, { plan_name: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-[11px]">Duração</Label>
                      <Select value={String(g.months)} onValueChange={(v) => upd(i, { months: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((m) => <SelectItem key={m} value={String(m)}>{m} {m === 1 ? 'mês' : 'meses'}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px]">Preço (R$)</Label>
                      <Input type="number" placeholder="30.00" value={g.price} onChange={(e) => upd(i, { price: e.target.value })} />
                    </div>
                  </div>
                  {(g.phone_updates || []).length > 0 && (
                    <p className="text-[11px] text-emerald-500 mt-2">📱 {g.phone_updates.length} telefone(s) serão atualizados do painel</p>
                  )}
                </div>
              ))}
            </div>
            <Button className="w-full" onClick={apply} disabled={applying}>
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Criar planos e vincular
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function guessMonths(name) {
  const n = (name || '').toLowerCase();
  const m = n.match(/(\d+)\s*(mes|mês)/);
  if (m) return String(Math.max(1, parseInt(m[1])));
  if (n.includes('anual') || n.includes('ano')) return '12';
  if (n.includes('semestral')) return '6';
  if (n.includes('trimestral')) return '3';
  if (n.includes('bimestral')) return '2';
  return '1';
}
